import type {
  Contact,
  ContactActivity,
  ContactActivityType,
  CrmActivityDomain,
} from '@otw/models/leads/contacts';
import { resolveCrmActivityDomain } from '@otw/models/leads/contacts';
import type {
  ContactCompany,
  ContactCompanyActivity,
} from '@otw/models/leads/contactCompanies';
import type { Lead } from '@otw/models/leads/lead';
import type { InventoryLead } from '@otw/models/leads/inventoryLead';
import { normalizeText } from './customerUtils';

export type CrmActivityDomainFilter = 'all' | CrmActivityDomain;

const toDateMs = (value: Date | string | null | undefined): number => {
  if (!value) return -Infinity;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? -Infinity : parsed.getTime();
};

/** Stable React key for a timeline row (activities have no persisted id). */
export const activityTimelineKey = (
  prefix: string,
  activity: {
    type: string;
    createdAt: Date;
    note?: string;
    leadId?: string;
    taskId?: string;
    contactId?: string;
  },
): string =>
  [
    prefix,
    activity.createdAt.toISOString(),
    activity.type,
    activity.leadId ?? '',
    activity.taskId ?? '',
    activity.contactId ?? '',
    (activity.note ?? '').slice(0, 48),
  ].join('|');

/**
 * Merged contact timeline — inverse of {@link ContactCompanyPreviewPane}:
 * contact activities (except `lead`) plus `lead` activities from linked companies.
 */
export type ContactMergedTimelineEntry =
  | { kind: 'created'; id: string; createdAt: Date; createdByUserId: string }
  | {
      kind: 'contact-activity';
      id: string;
      createdAt: Date;
      activity: ContactActivity;
    }
  | {
      kind: 'company-lead-activity';
      id: string;
      createdAt: Date;
      activity: ContactCompanyActivity;
      company: ContactCompany;
      createdByUserId: string;
    };

export type ContactMergedActivityFilter = 'all' | ContactActivityType | 'created';

export const CONTACT_MERGED_ACTIVITY_TITLE: Record<ContactActivityType, string> = {
  call: 'Logged a call',
  sms: 'Logged SMS',
  email: 'Logged an email',
  note: 'Note',
  task: 'Created a task',
  lead: 'Lead created',
  shipping_repairs: 'Shipping & Repairs',
};

/** Lead-created rows on a company doc only count when the lead belongs to that company. */
export function isLeadActivityForCompany(
  leadId: string | undefined,
  note: string | undefined,
  company: Pick<ContactCompany, 'id' | 'name'>,
  leadsById: Map<string, Lead>,
  inventoryLeadsById?: Map<string, InventoryLead>,
): boolean {
  const companyKey = normalizeText(company.name);
  if (leadId) {
    const inventoryLead = inventoryLeadsById?.get(leadId);
    if (inventoryLead) {
      return inventoryLead.companyId === company.id;
    }
    const lead = leadsById.get(leadId);
    if (lead) {
      if (lead.companyId && lead.companyId === company.id) return true;
      return normalizeText(lead.company) === companyKey;
    }
  }
  const noteCompany = note?.split('·')[0]?.trim();
  if (noteCompany) {
    return normalizeText(noteCompany) === companyKey;
  }
  return false;
}

export function buildContactMergedTimelineEntries(params: {
  contact: Contact;
  /** Companies this contact is a member of. */
  memberCompanies: ContactCompany[];
  leads?: Lead[];
  inventoryLeads?: InventoryLead[];
}): ContactMergedTimelineEntry[] {
  const { contact, memberCompanies, leads, inventoryLeads } = params;
  const leadsById = new Map((leads ?? []).map((lead) => [lead.id, lead]));
  const inventoryLeadsById = new Map(
    (inventoryLeads ?? []).map((lead) => [lead.id, lead]),
  );
  const entries: ContactMergedTimelineEntry[] = [
    {
      kind: 'created',
      id: 'contact-created',
      createdAt: contact.createdAt,
      createdByUserId: contact.createdByUserId,
    },
  ];

  for (const activity of contact.activities ?? []) {
    if (activity.type === 'lead') continue;
    entries.push({
      kind: 'contact-activity',
      id: activityTimelineKey('contact', activity),
      createdAt: activity.createdAt,
      activity,
    });
  }

  for (const company of memberCompanies) {
    for (const activity of company.activities ?? []) {
      if (activity.type !== 'lead') continue;
      if (
        !isLeadActivityForCompany(
          activity.leadId,
          activity.note,
          company,
          leadsById,
          inventoryLeadsById,
        )
      ) {
        continue;
      }
      entries.push({
        kind: 'company-lead-activity',
        id: activityTimelineKey(`company-${company.id}`, activity),
        createdAt: activity.createdAt,
        activity,
        company,
        createdByUserId: activity.createdByUserId,
      });
    }
  }

  return entries.sort((a, b) => toDateMs(b.createdAt) - toDateMs(a.createdAt));
}

export function contactMergedTimelineEntryAuthorId(
  entry: ContactMergedTimelineEntry,
): string | undefined {
  if (entry.kind === 'created') return entry.createdByUserId;
  if (entry.kind === 'contact-activity') return entry.activity.createdByUserId;
  return entry.createdByUserId;
}

export function getContactMergedTimelineActivityType(
  entry: ContactMergedTimelineEntry,
): ContactMergedActivityFilter {
  if (entry.kind === 'created') return 'created';
  if (entry.kind === 'contact-activity') return entry.activity.type;
  return 'lead';
}

export function getContactMergedTimelineDomain(
  entry: ContactMergedTimelineEntry,
): CrmActivityDomain | null {
  if (entry.kind === 'created') return null;
  if (entry.kind === 'contact-activity') {
    return resolveCrmActivityDomain(entry.activity.domain);
  }
  return resolveCrmActivityDomain(entry.activity.domain);
}

export function filterContactMergedTimelineEntries(
  entries: ContactMergedTimelineEntry[],
  activitySearch: string,
  activityFilter: ContactMergedActivityFilter,
  domainFilter: CrmActivityDomainFilter = 'all',
): ContactMergedTimelineEntry[] {
  const query = activitySearch.trim().toLowerCase();
  return entries.filter((entry) => {
    if (domainFilter !== 'all') {
      const domain = getContactMergedTimelineDomain(entry);
      // Synthetic created rows only appear under All
      if (domain === null || domain !== domainFilter) return false;
    }
    if (activityFilter !== 'all') {
      if (getContactMergedTimelineActivityType(entry) !== activityFilter) {
        return false;
      }
    }
    if (!query) return true;
    if (entry.kind === 'created') {
      return 'contact created'.includes(query);
    }
    if (entry.kind === 'contact-activity') {
      const title = CONTACT_MERGED_ACTIVITY_TITLE[entry.activity.type];
      return (
        title.toLowerCase().includes(query) ||
        entry.activity.note.toLowerCase().includes(query)
      );
    }
    const title = CONTACT_MERGED_ACTIVITY_TITLE.lead;
    return (
      title.toLowerCase().includes(query) ||
      entry.activity.note.toLowerCase().includes(query) ||
      entry.company.name.toLowerCase().includes(query)
    );
  });
}

export function getContactMergedTimelineDisplay(entry: ContactMergedTimelineEntry): {
  activityType: ContactActivityType | 'created';
  title: string;
  body: string | null;
  /** Set for lead activities logged on a linked company. */
  companyName: string | null;
} {
  if (entry.kind === 'created') {
    return {
      activityType: 'created',
      title: 'Contact created',
      body: null,
      companyName: null,
    };
  }
  if (entry.kind === 'contact-activity') {
    const domain = resolveCrmActivityDomain(entry.activity.domain);
    const note = entry.activity.note || '';
    let title = CONTACT_MERGED_ACTIVITY_TITLE[entry.activity.type];
    if (domain === 'marketing' && note.toLowerCase().startsWith('unsubscribed')) {
      title = 'Unsubscribed from marketing';
    } else if (domain === 'marketing' && entry.activity.type === 'email') {
      title = 'Campaign email sent';
    }
    return {
      activityType: entry.activity.type,
      title,
      body: note || null,
      companyName: null,
    };
  }
  return {
    activityType: 'lead',
    title: CONTACT_MERGED_ACTIVITY_TITLE.lead,
    body: entry.activity.note || null,
    companyName: entry.company.name,
  };
}
